import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useFormik } from 'formik';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { surveyDetail, surveyExport } from 'api/endpoints/survey';
import Breadcrumb from 'components/Breadcrumb';
import Button from 'components/Button';
import LayoutReview from 'components/layouts/LayoutReview';
import { ENUM_QUESTION_TYPE, ENUM_SURVEY_STATUS, ENUM_SURVEY_TYPE, ENUM_TYPE_ACTION, ENUM_TYPE_DOWNLOAD } from 'constants/enum';
import { TEXT_COMMON, TEXT_CREATE_SURVEY, TEXT_DOWNLOAD, TEXT_REPORT_LIST, TEXT_SURVEY_LIST } from 'constants/string/texts';
import { useClickOutside } from 'hooks/useClickOutside';
import useSurvey from 'hooks/useSurveys';
import { toastError } from 'lib/toast';
import AppScreenReview from 'pages/Survey/_components/AppScreenReview';
import Duplicate from 'pages/Survey/_components/Duplicate';
import Form from 'pages/Survey/Form/Form';
import AppRoutes from 'routes/routes';
import { clearModal, setLoading, setModal } from 'store/actions/common';
import { setOpenCancelSchedule, setOpenSchedule } from 'store/actions/survey';
import { checkIsShowScheduleText, convertStatusSurvey, convertTimePublic, getSurveyKindLabel, handleImageSurvey } from 'utils/helper';

const DownloadFile = require('assets/resources/DownloadFile.svg').default;
const MoreVert = require('assets/resources/MoreVert.svg').default;

const ViewSurvey = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const navigator = useNavigate();
  const dispatch = useDispatch();
  const { me } = useSelector((state) => state.auth);
  const controlSurveyRef = useRef(null);
  const [openControlSurvey, setIsOpenControlSurvey] = useState(false);
  const [openModalDuplicate, setOpenModalDuplicate] = useState(false);
  const { makePrivate, revertPublic, deleteSurvey } = useSurvey();
  const [breadcrumbs, setBreadcrumbs] = useState([
    {
      title: TEXT_CREATE_SURVEY.SURVEY_LIST,
      event: () => {
        navigator(AppRoutes.survey);
      },
    },
  ]);

  useClickOutside(controlSurveyRef, () => setIsOpenControlSurvey(false));

  const initialValues = (() => {
    const initData = {
      title: '',
      description: '',
      status: ENUM_SURVEY_STATUS.DRAFT,
      survey_type: ENUM_SURVEY_TYPE.EXAM,
      sections: [
        {
          title: '',
          order: 0,
          questions: [],
        },
      ],
    };

    return initData;
  })();

  const form = useFormik({
    initialValues: initialValues,
  });

  const handleImage = async (images) => {
    return await handleImageSurvey(images);
  };

  const handleQuestion = async (question) => {
    const processedImages = await handleImage(question.images);
    let options = [];
    if ([ENUM_QUESTION_TYPE.CHECKBOX, ENUM_QUESTION_TYPE.RADIO, ENUM_QUESTION_TYPE.DROPDOWN].includes(question.question_type)) {
      options = question.options.map((option) => ({ ...option, action: ENUM_TYPE_ACTION.UPDATE }));
    }

    return {
      ...question,
      action: ENUM_TYPE_ACTION.UPDATE,
      images: processedImages,
      options: options,
    };
  };

  const handleSection = async (section) => {
    const processedQuestions = [];
    for (const question of section.questions) {
      const questionHandled = await handleQuestion(question);
      processedQuestions.push(questionHandled);
    }

    return processedQuestions;
  };

  const handleSurvey = async (surveys) => {
    const dataSection = [];
    const sortedSections = _.sortBy(surveys.sections, 'order');
    for (const section of sortedSections) {
      const sectionHandled = await handleSection(section);
      dataSection.push({
        ...section,
        action: ENUM_TYPE_ACTION.UPDATE,
        questions: sectionHandled,
      });
    }

    if (surveys?.meta_data?.from_template) {
      for (const section of dataSection) {
        let questionCount = 0;
        for (const question of section.questions) {
          question.question_number = questionCount + 1;
          questionCount++;
        }
      }
    }

    if (breadcrumbs.length === 1) {
      let title = getSurveyKindLabel(surveys);
      if (surveys.is_template || surveys.meta_data?.from_template) {
        title = `${TEXT_COMMON.FV_TEMPLATE}${surveys.title}・${TEXT_REPORT_LIST.REPORT}`;
      }
      setBreadcrumbs([...breadcrumbs, { title: title }]);
    }

    form.setValues({ ...surveys, sections: dataSection });
  };

  const getDetail = async () => {
    try {
      dispatch(setLoading({ isOpen: true }));
      const result = await surveyDetail(id);
      if (result.data) {
        await handleSurvey(result.data);
      }
    } catch (error) {
      dispatch(setLoading({ isOpen: false }));
    } finally {
      dispatch(setLoading({ isOpen: false }));
    }
  };

  const getSurveyDetail = useCallback(async () => {
    if (me) {
      await getDetail();
    }
  }, [me, id]);

  const onDuplicateSurvey = () => {
    setOpenModalDuplicate(true);
  };

  const privateSurvey = async () => {
    try {
      makePrivate(form.values.id)
        .then(async () => {
          await getDetail();
        })
        .catch(() => {
          return false;
        });
    } catch {
      return false;
    }
  };

  const revertPublicSurvey = async () => {
    try {
      revertPublic(form.values.id, form.values.title)
        .then(async (response) => {
          navigator(AppRoutes.surveyViewFn(response.data.survey_id));
        })
        .catch(() => {
          return false;
        });
    } catch {
      return false;
    }
  };

  // Call open modal schedule
  const publicSurvey = async () => {
    dispatch(setOpenSchedule({ isOpen: true, data: form.values, callBack: getSurveyDetail }));
  };

  const onClickDeleteSurvey = () => {
    const confirmDeleteSurvey = async () => {
      try {
        deleteSurvey(form.values.id)
          .then(async () => {
            navigator(AppRoutes.survey);
            dispatch(clearModal());
          })
          .catch(() => {
            dispatch(clearModal());
          });
      } catch {
        dispatch(setLoading({ isOpen: false }));
      } finally {
        dispatch(setLoading({ isOpen: false }));
      }
    };

    dispatch(
      setModal({
        isOpen: true,
        type: 'delete',
        title: TEXT_SURVEY_LIST.CONFIRM_DELETE_SURVEY,
        confirmText: TEXT_COMMON.DELETE,
        cancelText: TEXT_COMMON.CANCEL_TEXT,
        onConfirm: confirmDeleteSurvey,
        onCancel: () => {
          dispatch(clearModal());
        },
      })
    );
  };

  const handleDownload = async (export_type) => {
    dispatch(setLoading({ isOpen: true }));
    try {
      await surveyExport({ survey_ids: [id] }, export_type, me.lang)
        .then(() => {
          dispatch(setLoading({ isOpen: false }));
        })
        .catch((error) => {
          toastError(error.message);
          dispatch(setLoading({ isOpen: false }));
        });
    } catch {
      dispatch(setLoading({ isOpen: false }));
    } finally {
      dispatch(setLoading({ isOpen: false }));
    }
  };

  const onClickCancelSchedule = () => {
    dispatch(setOpenCancelSchedule({ isOpen: true, id: id, callBack: getDetail }));
  };

  useEffect(() => {
    getSurveyDetail();
  }, [getSurveyDetail]);

  // Remove state openSchedule from create survey
  useEffect(() => {
    if (state?.openSchedule && form.values.id) {
      dispatch(setOpenSchedule({ isOpen: true, data: form.values, callBack: getSurveyDetail }));
      navigator(AppRoutes.surveyViewFn(id), { replace: true });
    }
  }, [state, id, form.values]);

  return (
    <>
      <section id="create-survey" className="flex-middle-start gap-6 relative">
        <LayoutReview className="z-2">
          <form className="create-survey">
            <div id="header-form" className="flex-between-center mb-1">
              <div className="flex items-center gap-2">
                <div className="max-w-80 flex items-center gap-2">
                  <Breadcrumb breadcrumbs={breadcrumbs} />
                </div>
                <span
                  className={`text-xs rounded-sm flex-middle-center border border-solid min-w-13 min-h-5.75 ${convertStatusSurvey(form.values.status)?.class}`}
                >
                  {convertStatusSurvey(form.values.status).name}
                </span>
              </div>

              <div className="flex items-center gap-8 relative z-2">
                <div className="flex items-center gap-4">
                  {form.values.status !== ENUM_SURVEY_STATUS.DRAFT && (
                    <>
                      <Button
                        onClick={() => handleDownload(ENUM_TYPE_DOWNLOAD.CSV)}
                        type="button"
                        color="outline"
                        className="text-black/85 text-sm w-55.5 h-8"
                        text={TEXT_DOWNLOAD.CSV_DOWNLOAD}
                        icon={<img className="mr-2.5" src={DownloadFile} alt="download file icon" width={14} height={14} />}
                      />
                      <Button
                        onClick={() => handleDownload(ENUM_TYPE_DOWNLOAD.EXCEL)}
                        type="button"
                        color="outline"
                        className="text-black/85 text-sm w-55.75 h-8"
                        text={TEXT_DOWNLOAD.EXCEL_DOWNLOAD}
                        icon={<img className="mr-2.5" src={DownloadFile} alt="download file icon" width={14} height={14} />}
                      />
                    </>
                  )}
                  <Button
                    onClick={onDuplicateSurvey}
                    type="button"
                    color="outline"
                    className="text-black/85 text-sm w-20 h-8"
                    text={TEXT_COMMON.DUPLICATE}
                  />
                  {form.values.status !== ENUM_SURVEY_STATUS.PUBLIC && (
                    <Link
                      to={`${AppRoutes.surveyEditFn(id)}`}
                      className="bg-primary-346 text-white text-black/85 text-sm w-20 h-8 flex justify-center items-center rounded-sm"
                    >
                      {TEXT_COMMON.EDIT}
                    </Link>
                  )}

                  <div className="relative flex items-center">
                    <button type="button" className="w-6 h-8" onClick={() => setIsOpenControlSurvey((prev) => !prev)} ref={controlSurveyRef}>
                      <img src={MoreVert} alt="more vert icon" width={24} height={24} />
                    </button>
                    <div className={`absolute right-0 py-1 bg-white shadow-soft-md animation-dropdown ${openControlSurvey ? 'show' : ''}`}>
                      <ul>
                        {form.values.status === ENUM_SURVEY_STATUS.PUBLIC && (
                          <li
                            onClick={privateSurvey}
                            className="px-3 w-24 py-1.75 text-danger-245 text-sm whitespace-nowrap hover:bg-white-795 cursor-pointer"
                          >
                            {TEXT_COMMON.UNPUBLIC_TEXT}
                          </li>
                        )}
                        {form.values.status === ENUM_SURVEY_STATUS.DRAFT && (
                          <li
                            onClick={publicSurvey}
                            className="px-3 w-24 py-1.25 text-black-102 text-sm whitespace-nowrap hover:bg-white-795 cursor-pointer"
                          >
                            {TEXT_CREATE_SURVEY.SAVE_PUBLIC}
                          </li>
                        )}
                        {form.values.status === ENUM_SURVEY_STATUS.PRIVATE && (
                          <li
                            onClick={revertPublicSurvey}
                            className="px-3 w-24 py-1.25 text-black-102 text-sm whitespace-nowrap hover:bg-white-795 cursor-pointer"
                          >
                            {TEXT_SURVEY_LIST.REVERT_PUBLIC}
                          </li>
                        )}
                        {form.values.status !== ENUM_SURVEY_STATUS.PUBLIC && (
                          <li
                            onClick={onClickDeleteSurvey}
                            className="px-3 w-24 py-1.75 text-danger-245 text-sm whitespace-nowrap hover:bg-white-795 cursor-pointer"
                          >
                            {TEXT_COMMON.DELETE}
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div id="public-schedule" className="flex items-center mb-8">
              <div className="flex items-center gap-2">
                {checkIsShowScheduleText(form.values.status, form.values.public_start_time, form.values.public_end_time) && (
                  <>
                    <span className="text-sm text-black/85">
                      {convertTimePublic(form.values.status, form.values.public_start_time, form.values.public_end_time)}
                    </span>
                    <button type="button" className="text-sm text-primary-346" onClick={onClickCancelSchedule}>
                      {TEXT_CREATE_SURVEY.CANCEL_PUBLIC_SETTING}
                    </button>
                  </>
                )}
              </div>
            </div>
            <Form form={form} viewOnly={true} />
          </form>
        </LayoutReview>
        <div className="sticky top-header-padding">
          <AppScreenReview form={form} />
        </div>
      </section>
      {openModalDuplicate && (
        <Duplicate
          form={form.values}
          close={() => {
            setOpenModalDuplicate(false);
          }}
        />
      )}
    </>
  );
};

export default React.memo(ViewSurvey);
